import {
  ThemeAsset,
  ThemeCollection,
  ThemeComponents,
  ThemeDefinition,
} from './Theme';

class ThemeProviderClass<
  ThemeName extends string,
  ThemeType extends ThemeComponents,
  DefaultTheme extends ThemeName = ThemeName,
> {
  private currentTheme: ThemeName;

  constructor(
    private themes: ThemeCollection<ThemeName, ThemeType>,
    currentTheme: DefaultTheme,
  ) {
    this.currentTheme = currentTheme;
  }

  public getTheme(
    name?: ThemeName,
  ): ThemeCollection<ThemeName, ThemeType>[ThemeName] {
    return this.themes[name ?? this.currentTheme];
  }

  public getOtherAssets(): ThemeAsset[] {
    const themes = Object.values(this.themes) as ThemeDefinition<ThemeType>[];
    return themes
      .filter((theme) => theme !== this.getTheme())
      .reduce(
        (assets, theme) => [...assets, ...theme.getAssets()],
        [] as ThemeAsset[],
      );
  }

  public setTheme(theme: ThemeName): void {
    this.currentTheme = theme;
  }
}

export function createThemeProvider<
  ThemeName extends string,
  ThemeType extends ThemeComponents,
  DefaultTheme extends ThemeName,
>(themes: ThemeCollection<ThemeName, ThemeType>, currentTheme: DefaultTheme) {
  // Return proxy that will pass functions through to the current theme.
  const provider = new ThemeProviderClass(themes, currentTheme);

  // Return a proxy that will pass functions through to the current theme.
  return new Proxy(provider, {
    get: (target, prop) => {
      if (prop in target) {
        return target[prop as keyof ThemeProviderClass<ThemeName, ThemeType>];
      }

      return target.getTheme()[
        prop as keyof ThemeCollection<ThemeName, ThemeType>
      ];
    },
  }) as ThemeProvider<ThemeName, ThemeType, DefaultTheme>;
}

export type ThemeProvider<
  ThemeName extends string,
  ThemeType extends ThemeComponents,
  DefaultTheme extends ThemeName = ThemeName,
> = ThemeProviderClass<ThemeName, ThemeType, DefaultTheme> & ThemeType;
